import { ArrowRight } from "@phosphor-icons/react";
import Image from "next/image";
import { useId } from "react";

import lucidityMark from "@/assets/logos/logo-simple-lucidity.svg";

export const FUNDRAISING_READINESS_TEST_URL = "https://tally.so/r/GxW7Lo";

// A markup copy of the Tally cover image, so it stays sharp at any size and
// can stack on narrow screens instead of being cropped. Wave shapes and colours
// are traced from the original artwork. It stretches to its parent's height,
// so it can sit level with a card beside it. `compact` scales the content
// down for slots where the full artwork proportions are too tall.
export default function FundraisingReadinessBanner({
  className = "",
  compact = false,
}) {
  // Gradient ids must be unique in case the banner renders twice on a page.
  const id = useId().replace(/:/g, "");

  return (
    <a
      href={FUNDRAISING_READINESS_TEST_URL}
      target='_blank'
      rel='noopener noreferrer'
      className={`relative flex items-center justify-center overflow-hidden rounded-xl bg-[#291253] px-6 py-12 [container-type:inline-size] ${compact ? "sm:px-[6%] sm:py-[2%]" : "sm:px-[8%] sm:py-[3%]"} ${className}`}
    >
      {/* Waves are sized off the banner's height, as in the artwork. */}
      <svg
        aria-hidden='true'
        viewBox='0 0 288 345'
        preserveAspectRatio='xMinYMin meet'
        className='absolute top-0 left-0 w-auto h-[48%]'
      >
        <defs>
          <linearGradient id={`${id}-tl`} x1='0.1' y1='0' x2='0.4' y2='1'>
            <stop offset='0' stopColor='#f2ac44' />
            <stop offset='0.5' stopColor='#e0457f' />
            <stop offset='1' stopColor='#c93a9f' />
          </linearGradient>
        </defs>
        <path
          d='M0 0H288C230 50 200 120 150 200C110 265 60 315 0 345Z'
          fill={`url(#${id}-tl)`}
        />
      </svg>

      <svg
        aria-hidden='true'
        viewBox='0 0 572 444'
        preserveAspectRatio='xMaxYMax meet'
        className='absolute bottom-0 right-0 w-auto h-[61%]'
      >
        <defs>
          <linearGradient id={`${id}-pink`} x1='1' y1='0' x2='0.3' y2='1'>
            <stop offset='0' stopColor='#ce3297' />
            <stop offset='0.5' stopColor='#83239f' />
            <stop offset='1' stopColor='#97239f' />
          </linearGradient>
          <linearGradient id={`${id}-blue`} x1='1' y1='0' x2='0' y2='1'>
            <stop offset='0' stopColor='#3d5ae8' />
            <stop offset='0.6' stopColor='#3541c7' />
            <stop offset='1' stopColor='#6d27a3' />
          </linearGradient>
        </defs>
        <path d='M572 0C520 110 400 330 0 444H572Z' fill={`url(#${id}-pink)`} />
        <path
          d='M572 140C500 230 330 390 200 444H572Z'
          fill={`url(#${id}-blue)`}
        />
      </svg>

      {/* From sm up, sizes are in container width units so the layout scales
          like the artwork instead of reflowing. The block is centred in the
          banner, so any spare width splits evenly on both sides. */}
      <div
        className={`relative flex flex-col items-center gap-5 text-center text-white sm:flex-row sm:items-center sm:text-left ${compact ? "sm:gap-[4cqw]" : "sm:gap-[5cqw]"}`}
      >
        {/* The app's mark plus a live-text wordmark, rather than a stacked
            SVG whose paths kept getting clipped by its viewBox. Sized so the
            block is as tall as the title and subtitle beside it. */}
        <div className='flex flex-col items-center flex-shrink-0'>
          <Image
            src={lucidityMark}
            alt=''
            className={`w-auto h-16 sm:h-auto ${compact ? "sm:w-[9.8cqw]" : "sm:w-[13.7cqw]"}`}
          />
          {/* Outfit Medium, with the logo's line spacing and indented
              second line. */}
          <p
            className={`mt-2 text-base font-medium leading-[0.87] text-left font-display ${compact ? "sm:mt-[1.4cqw] sm:text-[2.83cqw]" : "sm:mt-[1.7cqw] sm:text-[3.9cqw]"}`}
          >
            Lucidity
            <span className='block pl-[0.3em]'>Insights</span>
          </p>
        </div>
        <div>
          <p
            className={`text-3xl font-extrabold leading-[0.95] uppercase font-display sm:leading-[0.93] sm:tracking-[0.01em] ${compact ? "sm:text-[5.6cqw]" : "sm:text-[7.8cqw]"}`}
          >
            Fundraising
            <br />
            Readiness Test
          </p>
          <p
            className={`mt-3 text-sm leading-snug font-display sm:leading-[1.3] ${compact ? "sm:mt-[1.5cqw] sm:text-[2.3cqw]" : "sm:mt-[2cqw] sm:text-[3.1cqw]"}`}
          >
            Are you a Founder thinking about fundraising?
            <br />
            Take the Free Test to see if you&rsquo;re ready.
          </p>
          {/* The whole banner is the link, so this only looks like a button. */}
          <span
            className={`inline-flex items-center gap-2 mt-5 px-5 py-2.5 text-sm font-semibold tracking-wide uppercase rounded-lg bg-white text-[#291253] font-display ${compact ? "sm:mt-[1.6cqw] sm:gap-[0.8cqw] sm:px-[1.8cqw] sm:py-[0.8cqw] sm:text-[1.6cqw] sm:rounded-[0.6cqw]" : "sm:mt-[2.2cqw] sm:gap-[1cqw] sm:px-[2.4cqw] sm:py-[1.1cqw] sm:text-[2.1cqw] sm:rounded-[0.8cqw]"}`}
          >
            Take Test
            <ArrowRight weight='bold' />
          </span>
        </div>
      </div>
    </a>
  );
}
